import React, { useCallback, useRef } from 'react'
import type { DataMenuMainElementDeclaration } from '@dx-private/dxchart5-react/dist/chart/ui-overrides/rc-menus'
import { LicensedChartWidgetHost } from './components/LicensedChartWidgetHost'
import { useTournamentConnection, type AppError } from './hooks/useTournamentConnection'
import { tradeDisplayLog, chartDebugLog } from './lib/debug/browser-logging'
import { applyReadonlyTradingSettings } from './lib/chart/readonly-trading'
import {
  ALLOWED_PERIODS,
  type TournamentAggregationPeriod,
} from './lib/chart/create-tournament-providers'
import { resolveTradingItemOperationId } from './lib/chart/resolve-trading-item-operation-id'
import { ErrorCard } from './components/ErrorCard'
import { WidgetShell } from './layout/WidgetShell'
import { selectPendingOrdersBySymbol, selectOpenPositionsBySymbol, selectOperationById } from './lib/store'
import { isLocalPlayerOperation } from './lib/store/operation-owner'

const TOURNAMENT_DATA_MENU_ELEMENTS: DataMenuMainElementDeclaration[] = [
  'ChartTypePopover',
  'PriceTypePopover',
  'ColorPickerItem',
  'ReorderDivider',
  'BringToFrontButton',
  'SendToBackButton',
]

const TOURNAMENT_BASE_UI_OVERRIDES = {
  rightClickMenus: {
    DataMenuElements: TOURNAMENT_DATA_MENU_ELEMENTS,
  },
}

const ORDER_ITEM_SELECTOR = "[data-test-id^='order_item_'], [data-test-id^='position_item_']"
const ORDER_SECTION_SELECTOR = "[class*='DXChart-OrderSectionStyled']"
const ALLOWED_PERIOD_KEYS = new Set(ALLOWED_PERIODS.map((period) => `${period.duration}:${period.durationType}`))

const isAllowedPeriod = (period: TournamentAggregationPeriod | null | undefined): boolean => {
  if (!period || typeof period.duration !== 'number' || typeof period.durationType !== 'string') {
    return false
  }

  return ALLOWED_PERIOD_KEYS.has(`${period.duration}:${period.durationType}`)
}

const formatOrderQuantity = (text: string): string => {
  const trimmed = text.trim()
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(trimmed)) {
    return text
  }
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) {
    return text
  }
  return `${parsed.toFixed(1)}`
}

const formatPositionPnl = (text: string): string => {
  const trimmed = text.trim()
  const normalized = trimmed.replace(/\u2212/g, '-')
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(normalized)) {
    return text
  }
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) {
    return text
  }
  const isNegative = parsed < 0 || Object.is(parsed, -0) || normalized.startsWith('-')
  const sign = isNegative ? '-' : '+'
  return `${sign}${Math.abs(parsed).toFixed(2)}`
}

const normalizeTradingPillText = (host: ParentNode): void => {
  const items = host.querySelectorAll<HTMLElement>(ORDER_ITEM_SELECTOR)
  items.forEach((item) => {
    const sections = item.querySelectorAll<HTMLElement>(ORDER_SECTION_SELECTOR)
    if (sections.length === 0) {
      return
    }

    // Quantity section is consistently the first section in both order and position pills.
    const quantityEl = sections[0]
    const nextQuantity = formatOrderQuantity(quantityEl.textContent ?? '')
    if ((quantityEl.textContent ?? '') !== nextQuantity) {
      quantityEl.textContent = nextQuantity
    }

    // Normalize signed numeric PnL-like section to 2 decimals.
    // We scan all sections after quantity to tolerate vendor DOM shifts.
    for (let i = 1; i < sections.length; i += 1) {
      const sectionEl = sections[i]
      const raw = sectionEl.textContent ?? ''
      const next = formatPositionPnl(raw)
      if (raw !== next) {
        sectionEl.textContent = next
        break
      }
    }
  })
}

interface TournamentChartState {
  loading: boolean
  error: AppError | null
}

interface TournamentChartDebugSnapshot {
  chartId: string | null
  instrument: string | null
  tradingSettings: unknown
  chartReactConfigTrading: unknown
  initialChartTrading: unknown
  providerDiagnostics: {
    symbol: string | null
    orders: unknown
    positions: unknown
  }
}

interface TournamentChartDebugHandle {
  api: any
  dependencies: any
  getSelectedChartInfo: () => any
  getTradingSnapshot: (symbol?: string) => TournamentChartDebugSnapshot
  getReadonlyDiagnostics: (symbol?: string) => TournamentChartDebugSnapshot['providerDiagnostics']
  enforceReadonlyTradingSettings: (reason?: string) => void
}

declare global {
  interface Window {
    __utcTournamentChartDebug?: TournamentChartDebugHandle
  }
}

const getSelectedChartInfoSafe = (api: any): any => {
  if (!api || typeof api.getSelectedChartInfo !== 'function') {
    return null
  }

  return api.getSelectedChartInfo()
}

const getOperationIdFromTradingItem = (item: any): string | null => {
  const operationId =
    typeof item?.model?.data?.operationId === 'string'
      ? item.model.data.operationId
      : typeof item?.data?.operationId === 'string'
        ? item.data.operationId
        : null

  return operationId && operationId.length > 0 ? operationId : null
}


const getSelectedInstrumentSymbol = (selectedChartInfo: any): string | null => {
  return typeof selectedChartInfo?.instrument === 'string' && selectedChartInfo.instrument.length > 0
    ? selectedChartInfo.instrument
    : null
}

const resolveBackendSymbol = (
  symbol: string | null,
  symbolFullNames?: Record<string, string>,
): string | null => {
  if (!symbol) {
    return null
  }

  if (!symbolFullNames) {
    return symbol
  }

  if (symbolFullNames[symbol]) {
    return symbol
  }

  for (const [backendSymbol, fullSymbol] of Object.entries(symbolFullNames)) {
    if (fullSymbol === symbol) {
      return backendSymbol
    }
  }

  return symbol
}

const summarizeTradingItem = (item: any): Record<string, unknown> => ({
  type: typeof item?.type === 'string' ? item.type : null,
  selected: typeof item?.selected === 'boolean' ? item.selected : null,
  modelId: typeof item?.model?.id === 'string' ? item.model.id : null,
  modelType: typeof item?.model?.type === 'string' ? item.model.type : null,
  originalItemId:
    typeof item?.model?.originalItemId === 'string' ? item.model.originalItemId : null,
  operationId: getOperationIdFromTradingItem(item),
  protectionOrderIds: Array.isArray(item?.model?.protectionOrderIds)
    ? item.model.protectionOrderIds
    : null,
  dataKeys:
    item?.model?.data && typeof item.model.data === 'object'
      ? Object.keys(item.model.data)
      : item?.data && typeof item.data === 'object'
        ? Object.keys(item.data)
        : null,
})

const buildProviderDiagnostics = (
  dependencies: any,
  selectedChartInfo: any,
  explicitSymbol?: string,
): TournamentChartDebugSnapshot['providerDiagnostics'] => {
  const resolvedSymbol =
    (typeof explicitSymbol === 'string' && explicitSymbol.length > 0
      ? explicitSymbol
      : getSelectedInstrumentSymbol(selectedChartInfo)) ??
    (typeof dependencies?.initialInstrument === 'string' && dependencies.initialInstrument.length > 0
      ? dependencies.initialInstrument
      : null)

  const orderProvider = dependencies?.orderProvider as any
  const positionProvider = dependencies?.positionProvider as any

  return {
    symbol: resolvedSymbol,
    orders:
      resolvedSymbol && typeof orderProvider?.getReadonlyDiagnostics === 'function'
        ? orderProvider.getReadonlyDiagnostics(resolvedSymbol)
        : null,
    positions:
      resolvedSymbol && typeof positionProvider?.getReadonlyDiagnostics === 'function'
        ? positionProvider.getReadonlyDiagnostics(resolvedSymbol)
        : null,
  }
}

const buildTradingDebugSnapshot = (
  api: any,
  dependencies: any,
  symbol?: string,
): TournamentChartDebugSnapshot => {
  const selectedChartInfo = getSelectedChartInfoSafe(api)

  return {
    chartId: typeof selectedChartInfo?.id === 'string' ? selectedChartInfo.id : null,
    instrument: getSelectedInstrumentSymbol(selectedChartInfo),
    tradingSettings: selectedChartInfo?.chartSettings?.chartReact?.trading ?? null,
    chartReactConfigTrading: dependencies?.chartReactConfig?.trading ?? null,
    initialChartTrading: dependencies?.initialChartReactSettings?.trading ?? null,
    providerDiagnostics: buildProviderDiagnostics(dependencies, selectedChartInfo, symbol),
  }
}

const getInternalChartVMEntries = (api: any): Array<{ chartId: string; chartVM: any }> => {
  const chartVMs = api?.internal?.chartVMs

  if (Array.isArray(chartVMs)) {
    return chartVMs.map((chartVM, index) => ({
      chartId: String(index),
      chartVM,
    }))
  }

  if (chartVMs && typeof chartVMs === 'object') {
    return Object.entries(chartVMs).map(([chartId, chartVM]) => ({
      chartId,
      chartVM,
    }))
  }

  return []
}

const findTradingItemFromTradingVM = (tradingVM: any, itemId: string): any => {
  const groupedItems = tradingVM?.groupedVisualTradingItems?.getValue?.()
  if (groupedItems && typeof groupedItems === 'object') {
    for (const group of Object.values(groupedItems) as any[]) {
      const match = Array.isArray(group?.items)
        ? group.items.find((item: any) => item?.model?.id === itemId)
        : null
      if (match) {
        return match
      }
    }
  }

  const collections = [
    tradingVM?.orders?.getValue?.(),
    tradingVM?.positions?.getValue?.(),
    tradingVM?.editableOrders?.getValue?.(),
    tradingVM?.editablePositions?.getValue?.(),
  ]

  for (const collection of collections) {
    if (collection && typeof collection === 'object' && collection[itemId]) {
      return collection[itemId]
    }
  }

  return null
}

export function TournamentChart() {
  const { dependencies, error, isReady, chartRemountKey } = useTournamentConnection()
  const [state, setState] = React.useState<TournamentChartState>({
    loading: !isReady && !error,
    error,
  })
  const [chartApi, setChartApi] = React.useState<any>(null)
  const [selectedOperationId, setSelectedOperationId] = React.useState<string | null>(null)
  const [debugMode] = React.useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('debug') === 'true'
  })
  const chartApiRef = useRef<any>(null)
  const readonlySettingsEnforcementRafRef = useRef<number | null>(null)
  const settingsChangedUnsubscribeRef = useRef<(() => void) | null>(null)
  const layoutRestorationCleanupRef = useRef<(() => void) | null>(null)
  const clickUnsubscribesRef = useRef<Array<() => void>>([])
  const groupSelectionRestoreRef = useRef<Array<() => void>>([])
  const lastSelectedSymbolRef = useRef<string | null>(null)
  const selectedOperationIdRef = useRef<string | null>(null)
  const previousActiveOperationIdsRef = useRef<Set<string>>(new Set())

  // Update state when connection result changes
  React.useEffect(() => {
    if (isReady) {
      setState({ loading: false, error: null })
    } else if (error) {
      setState({ loading: false, error })
    } else {
      setState({ loading: true, error: null })
    }
  }, [isReady, error])

  React.useEffect(() => {
    selectedOperationIdRef.current = selectedOperationId
  }, [selectedOperationId])

  const enforceReadonlyTradingSettings = useCallback((api: any, reason: string) => {
    if (
      !api ||
      typeof api.getSelectedChartInfo !== 'function' ||
      typeof api.setChartSettings !== 'function'
    ) {
      return
    }

    const selectedChartInfo = api.getSelectedChartInfo()
    const chartSettings = selectedChartInfo?.chartSettings
    if (!chartSettings) {
      return
    }

    const { changed, nextSettings } = applyReadonlyTradingSettings(chartSettings)
    tradeDisplayLog('readonly-trading settings check', {
      reason,
      chartId: typeof selectedChartInfo?.id === 'string' ? selectedChartInfo.id : null,
      instrument: getSelectedInstrumentSymbol(selectedChartInfo),
      trading: chartSettings?.chartReact?.trading ?? null,
      changed,
    })
    if (!changed) {
      return
    }

    api.setChartSettings(
      nextSettings,
      typeof selectedChartInfo?.id === 'string' ? selectedChartInfo.id : undefined,
    )
    tradeDisplayLog('readonly-trading settings applied', {
      reason,
      chartId: typeof selectedChartInfo?.id === 'string' ? selectedChartInfo.id : null,
      instrument: getSelectedInstrumentSymbol(selectedChartInfo),
      trading: nextSettings?.chartReact?.trading ?? null,
    })
  }, [])

  const scheduleReadonlyTradingSettingsEnforcement = useCallback((api: any, reason: string) => {
    if (readonlySettingsEnforcementRafRef.current !== null) {
      return
    }

    readonlySettingsEnforcementRafRef.current = requestAnimationFrame(() => {
      readonlySettingsEnforcementRafRef.current = null
      enforceReadonlyTradingSettings(api, reason)
    })
  }, [enforceReadonlyTradingSettings])

  const extractOperationIdFromTradingItem = useCallback((itemOrId: any, fallbackItemId?: string): string | null => {
    const operationId = resolveTradingItemOperationId(itemOrId, fallbackItemId)

    if (!operationId) {
      tradeDisplayLog('selection ignored: trading item missing resolvable operationId', {
        item: typeof itemOrId === 'object' ? summarizeTradingItem(itemOrId) : null,
        providedId: typeof itemOrId === 'string' ? itemOrId : null,
        fallbackItemId,
      })
    }

    return operationId
  }, [])

  const updateProviderSelection = useCallback((operationId: string | null) => {
    if (!dependencies) {
      return
    }
    const orderProvider = dependencies.orderProvider
    const positionProvider = dependencies.positionProvider
    if (orderProvider && typeof orderProvider.setVisibleSelection === 'function') {
      orderProvider.setVisibleSelection(operationId, false)
    }
    if (positionProvider && typeof positionProvider.setVisibleSelection === 'function') {
      positionProvider.setVisibleSelection(operationId, false)
    }
  }, [dependencies])

  const clearSelection = useCallback(() => {
    tradeDisplayLog('selection cleared', {
      previousSelectedOperationId: selectedOperationId,
    })
    setSelectedOperationId(null)
    updateProviderSelection(null)
  }, [selectedOperationId, updateProviderSelection])

  const applySelection = useCallback((operationId: string) => {
    // Validate that the operation belongs to the local player
    const store = dependencies?.__TOURNAMENT_STORE__
    const localUserId = dependencies?.__LOCAL_USER_ID__
    const operation = store ? selectOperationById(store.getState(), operationId) : undefined

    if (!operation || (localUserId && !isLocalPlayerOperation(operation, localUserId))) {
      tradeDisplayLog('selection rejected: non-local or missing operation', {
        operationId,
        hasOperation: !!operation,
        localUserId,
      })
      return
    }

    tradeDisplayLog('selection applied', {
      previousSelectedOperationId: selectedOperationId,
      nextSelectedOperationId: operationId,
    })
    setSelectedOperationId(operationId)
    updateProviderSelection(operationId)
  }, [selectedOperationId, updateProviderSelection, dependencies])

  const unsubscribeClickHandlers = useCallback(() => {
    clickUnsubscribesRef.current.forEach((unsubscribe) => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    })
    clickUnsubscribesRef.current = []
  }, [])

  const restoreGroupSelectionHandlers = useCallback(() => {
    groupSelectionRestoreRef.current.forEach((restore) => {
      if (typeof restore === 'function') {
        restore()
      }
    })
    groupSelectionRestoreRef.current = []
  }, [])

  const registerGroupItemSelectionBridge = useCallback((api: any) => {
    restoreGroupSelectionHandlers()

    const restorers: Array<() => void> = []
    for (const { chartId, chartVM } of getInternalChartVMEntries(api)) {
      const tradingVM = chartVM?.tradingVM
      const originalOnGroupItemSelect = tradingVM?.onGroupItemSelect

      if (typeof originalOnGroupItemSelect !== 'function') {
        continue
      }

      const wrappedOnGroupItemSelect = (itemId: string) => {
        const item = findTradingItemFromTradingVM(tradingVM, itemId)

        tradeDisplayLog('group item select received', {
          chartId,
          selectedItemId: itemId,
          item: summarizeTradingItem(item),
        })

        if (item) {
          tradeDisplayLog('group item selection bridged', {
            chartId,
            item: summarizeTradingItem(item),
          })
        }

        // Resolve operation id from item if found, or try the provided itemId directly
        const operationId = extractOperationIdFromTradingItem(item || itemId, itemId)
        if (operationId) {
          applySelection(operationId)
        } else {
          tradeDisplayLog('group item select unresolved', {
            chartId,
            selectedItemId: itemId,
          })
        }

        return originalOnGroupItemSelect.call(tradingVM, itemId)
      }

      tradingVM.onGroupItemSelect = wrappedOnGroupItemSelect
      restorers.push(() => {
        if (tradingVM.onGroupItemSelect === wrappedOnGroupItemSelect) {
          tradingVM.onGroupItemSelect = originalOnGroupItemSelect
        }
      })
    }

    groupSelectionRestoreRef.current = restorers
  }, [applySelection, extractOperationIdFromTradingItem, restoreGroupSelectionHandlers])

  const registerChartApiInteractions = useCallback((api: any) => {
    unsubscribeClickHandlers()

    const unsubscribes: Array<() => void> = []
    const selectedChartInfo = getSelectedChartInfoSafe(api)
    lastSelectedSymbolRef.current = getSelectedInstrumentSymbol(selectedChartInfo)

    tradeDisplayLog('chart api interactions registered', {
      chartId: typeof selectedChartInfo?.id === 'string' ? selectedChartInfo.id : null,
      instrument: getSelectedInstrumentSymbol(selectedChartInfo),
      supportsOrderClick: typeof api?.onOrderClick === 'function',
      supportsPositionClick: typeof api?.onPositionClick === 'function',
      supportsInstrumentChanged: typeof api?.onInstrumentChanged === 'function',
    })

    if (typeof api.onOrderClick === 'function') {
      const unsubscribe = api.onOrderClick((_chartId: string, visualItem: any) => {
        tradeDisplayLog('order click received', {
          item: summarizeTradingItem(visualItem),
        })
        const operationId = extractOperationIdFromTradingItem(visualItem)
        if (operationId) {
          applySelection(operationId)
        }
      })
      if (typeof unsubscribe === 'function') {
        unsubscribes.push(unsubscribe)
      }
    }

    if (typeof api.onPositionClick === 'function') {
      const unsubscribe = api.onPositionClick((_chartId: string, visualItem: any) => {
        tradeDisplayLog('position click received', {
          item: summarizeTradingItem(visualItem),
        })
        const operationId = extractOperationIdFromTradingItem(visualItem)
        if (operationId) {
          applySelection(operationId)
        }
      })
      if (typeof unsubscribe === 'function') {
        unsubscribes.push(unsubscribe)
      }
    }

    if (typeof api.onInstrumentChanged === 'function') {
      const unsubscribe = api.onInstrumentChanged((_chartId: string, instrument: any) => {
        const nextSymbol =
          typeof instrument?.symbol === 'string' && instrument.symbol.length > 0
            ? instrument.symbol
            : null

        tradeDisplayLog('instrument changed', {
          previousSymbol: lastSelectedSymbolRef.current,
          nextSymbol,
          selectedOperationId: selectedOperationIdRef.current,
        })

        if (lastSelectedSymbolRef.current !== null && lastSelectedSymbolRef.current !== nextSymbol) {
          clearSelection()
        }

        lastSelectedSymbolRef.current = nextSymbol
      })
      if (typeof unsubscribe === 'function') {
        unsubscribes.push(unsubscribe)
      }
    }

    clickUnsubscribesRef.current = unsubscribes
  }, [applySelection, clearSelection, extractOperationIdFromTradingItem, unsubscribeClickHandlers])

  const handleApiCreated = useCallback((api: any) => {
    chartApiRef.current = api
    setChartApi(api)

    const requestedInitialInstrument =
      typeof dependencies?.initialInstrument === 'string' && dependencies.initialInstrument.length > 0
        ? dependencies.initialInstrument
        : null
    const selectedChartInfo = getSelectedChartInfoSafe(api)
    const currentInstrument = getSelectedInstrumentSymbol(selectedChartInfo)

    if (
      requestedInitialInstrument &&
      typeof api.changeInstrument === 'function' &&
      currentInstrument !== requestedInitialInstrument
    ) {
      chartDebugLog('tournament', 'forcing initial instrument after API creation', {
        currentInstrument,
        requestedInitialInstrument,
      })
      api.changeInstrument(requestedInitialInstrument)
    }

    // Synchronize chart API with data provider and trigger initial refresh (Task 3.1, 3.2)
    if (dependencies?.chartDataProvider) {
      dependencies.chartDataProvider.setChartApi(api)
      dependencies.chartDataProvider.refreshChart()
    }

    // Set viewport for historical matches: 4h back / 8h forward from current replay time
    // This ensures the chart centers on the replay time both on initial load and after reset
    if (dependencies?.__IS_HISTORICAL__ && typeof api.setVisibleRange === 'function') {
      const store = dependencies.__TOURNAMENT_STORE__
      const replayTimeIso =
        store?.getState()?.currentReplayTimeDisplay ?? store?.getState()?.currentReplayTime
      const startMs = replayTimeIso ? Date.parse(replayTimeIso) : (dependencies.__START_DATE__ ? Date.parse(dependencies.__START_DATE__) : NaN)

      if (!Number.isNaN(startMs)) {
        chartDebugLog('tournament', 'setting visible range for historical match', { startMs })
        api.setVisibleRange({
          from: startMs - 1000 * 60 * 60 * 4,
          to: startMs + 1000 * 60 * 60 * 8,
        })
      }
    } else if (!dependencies?.__IS_HISTORICAL__ && typeof api.setVisibleRange === 'function') {
      // Set viewport for live matches: 4h back / right-aligned to current time
      // This allows users to see recent history and the current moment when resetting in a live match
      const nowMs = Date.now()
      chartDebugLog('tournament', 'setting visible range for live match', { nowMs })
      api.setVisibleRange({
        from: nowMs - 1000 * 60 * 60 * 4,
        to: nowMs,
      })
    }

    // Lock to 1m aggregation for all historical tournaments (queueType=Historical)
    const isHistoricalMode = dependencies?.__IS_HISTORICAL__ === true

    registerChartApiInteractions(api)
    registerGroupItemSelectionBridge(api)
    window.__utcTournamentChartDebug = {
      api,
      dependencies,
      getSelectedChartInfo: () => getSelectedChartInfoSafe(api),
      getTradingSnapshot: (symbol?: string) => buildTradingDebugSnapshot(api, dependencies, symbol),
      getReadonlyDiagnostics: (symbol?: string) =>
        buildTradingDebugSnapshot(api, dependencies, symbol).providerDiagnostics,
      enforceReadonlyTradingSettings: (reason = 'window-debug') =>
        enforceReadonlyTradingSettings(api, reason),
    }

    if (isHistoricalMode && typeof api.setAggregationRestrictionRule === 'function') {
      api.setAggregationRestrictionRule((period: any) => {
        return period.duration === 1 && period.durationType === 'm'
      })
      if (typeof api.changePeriod === 'function') {
        api.changePeriod({ duration: 1, durationType: 'm' })
      }
    }

    if (!isHistoricalMode && typeof api.setAggregationRestrictionRule === 'function') {
      api.setAggregationRestrictionRule((period: TournamentAggregationPeriod) => {
        return isAllowedPeriod(period)
      })
    }

    tradeDisplayLog('chart api created', buildTradingDebugSnapshot(api, dependencies))

    // Apply readonly settings at startup
    scheduleReadonlyTradingSettingsEnforcement(api, 'startup')

    // Apply periodic enforcement during layout restoration window (first 2 seconds)
    // to catch async layout loading that would otherwise restore showPositions: false
    const layoutRestorationEnforcementIntervalId = window.setInterval(() => {
      enforceReadonlyTradingSettings(api, 'layout-restoration-window')
    }, 200)

    const layoutRestorationTimeoutId = window.setTimeout(() => {
      window.clearInterval(layoutRestorationEnforcementIntervalId)
    }, 2000)

    // Set up re-enforcement after settings changes
    if (settingsChangedUnsubscribeRef.current) {
      settingsChangedUnsubscribeRef.current()
    }

    const chartVMs = api?.internal?.chartVMs
    if (!chartVMs) {
      return
    }

    let settingsChangedUnsubscribe: (() => void) | null = null

    if (Array.isArray(chartVMs)) {
      const chartVM = chartVMs[0]
      if (chartVM?.chart?.settings?.subscribe) {
        settingsChangedUnsubscribe = chartVM.chart.settings.subscribe(() => {
          tradeDisplayLog('chart settings changed', buildTradingDebugSnapshot(api, dependencies))
          scheduleReadonlyTradingSettingsEnforcement(api, 'settings-changed')
        })
      }
    } else if (typeof chartVMs === 'object') {
      const chartVM = Object.values(chartVMs)[0] as any
      if (chartVM?.chart?.settings?.subscribe) {
        settingsChangedUnsubscribe = chartVM.chart.settings.subscribe(() => {
          tradeDisplayLog('chart settings changed', buildTradingDebugSnapshot(api, dependencies))
          scheduleReadonlyTradingSettingsEnforcement(api, 'settings-changed')
        })
      }
    }

    if (settingsChangedUnsubscribe) {
      settingsChangedUnsubscribeRef.current = settingsChangedUnsubscribe
    }

    // Store cleanup function for the interval/timeout
    layoutRestorationCleanupRef.current = () => {
      window.clearInterval(layoutRestorationEnforcementIntervalId)
      window.clearTimeout(layoutRestorationTimeoutId)
    }

  }, [
    dependencies,
    enforceReadonlyTradingSettings,
    registerGroupItemSelectionBridge,
    registerChartApiInteractions,
    scheduleReadonlyTradingSettingsEnforcement,
  ])

  React.useEffect(() => {
    tradeDisplayLog('selected operation state updated', {
      selectedOperationId,
      chartInstrument: chartApi ? getSelectedInstrumentSymbol(getSelectedChartInfoSafe(chartApi)) : null,
    })
  }, [chartApi, selectedOperationId])

  React.useEffect(() => {
    const host = document.getElementById('chart-react-wrapper')
    if (!host) {
      return
    }

    normalizeTradingPillText(host)

    const observer = new MutationObserver(() => {
      normalizeTradingPillText(host)
    })

    observer.observe(host, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [chartApi])

  // Capture internal tradingVM visual selections via document-level capture
  // so listener survives vendor remounts of the chart wrapper node.
  React.useEffect(() => {
    const handleClickCapture = (event: Event) => {
      const target = event.target as HTMLElement
      if (!target) return

      // Find the closest trading item element (position, order, or protection order)
      const item = target.closest(
        '[data-test-id^="position_item_"], [data-test-id^="order_item_"], [data-test-id^="protection_order_item_"]',
      ) as HTMLElement
      if (!item) return

      const testId = item.getAttribute('data-test-id')
      if (!testId) return

      // Extract operation id from test id string, handling protection items
      // position_item_abc123 -> abc123
      // order_item_abc123 -> abc123
      // protection_order_item_abc123::tp -> abc123 (extract parent from protection)
      let operationId: string | null = null

      if (testId.startsWith('position_item_')) {
        operationId = testId.substring('position_item_'.length)
      } else if (testId.startsWith('order_item_')) {
        operationId = testId.substring('order_item_'.length)
      } else if (testId.startsWith('protection_order_item_')) {
        // For protection items, extract the parent operation id (part before ::tp or ::sl)
        const withoutPrefix = testId.substring('protection_order_item_'.length)
        const parentMatch = withoutPrefix.match(/^(.+?)::(tp|sl)$/)
        operationId = parentMatch ? parentMatch[1] : withoutPrefix
      }

      if (operationId) {
        tradeDisplayLog('trading item click captured via DOM', {
          testId,
          extractedOperationId: operationId,
        })
        applySelection(operationId)
      }
    }

    document.addEventListener('mousedown', handleClickCapture, true)
    document.addEventListener('click', handleClickCapture, true)

    return () => {
      document.removeEventListener('mousedown', handleClickCapture, true)
      document.removeEventListener('click', handleClickCapture, true)
    }
  }, [applySelection])

  // Subscribe to operation store to validate selection and detect new operations (Task 2.3, 4.1-4.3)
  React.useEffect(() => {
    if (!dependencies || !chartApi) {
      return
    }

    const store = dependencies.__TOURNAMENT_STORE__
    if (!store) {
      return
    }

    const api = chartApi

    // Check if the selected operation is still active for the current symbol (Task 2.3)
    // Also detect newly created operations and auto-focus them if they match the current symbol (Task 4.1-4.3)
    const handleStoreUpdate = () => {
      const selectedChartInfo = getSelectedChartInfoSafe(api)
      const sym = resolveBackendSymbol(
        getSelectedInstrumentSymbol(selectedChartInfo),
        dependencies.__SYMBOL_FULL_NAMES__,
      )

      if (!sym) {
        return
      }

      const state = store.getState()
      const pendingBySymbol = selectPendingOrdersBySymbol(state, sym)
      const openBySymbol = selectOpenPositionsBySymbol(state, sym)

      const activePendingOrders = pendingBySymbol.filter((order) => order.status === 'ACTIVE')
      const currentActiveOperationIds = new Set([
        ...activePendingOrders.map((order) => order.id),
        ...openBySymbol.map((position) => position.order.id),
      ])

      // Validate existing selection (clear if no longer active)
      if (selectedOperationIdRef.current) {
        const isPending = pendingBySymbol.some(
          (order) => order.id === selectedOperationIdRef.current && order.status === 'ACTIVE'
        )
        const isOpen = openBySymbol.some((opOpen) => opOpen.order.id === selectedOperationIdRef.current)

        if (!isPending && !isOpen) {
          tradeDisplayLog('selection cleared: operation no longer active', {
            operationId: selectedOperationIdRef.current,
            backendSymbol: sym,
          })
          clearSelection()
        }
      }

      // Auto-focus only when a brand-new operation appears on the current symbol.
      // This covers newly placed pending orders and newly placed market/open orders.
      const newlyCreatedCandidates: Array<{ id: string; createdAtMs: number }> = [
        ...activePendingOrders
          .filter((order) => !previousActiveOperationIdsRef.current.has(order.id))
          .map((order) => ({
            id: order.id,
            createdAtMs: Date.parse(order.placedAt),
          })),
        ...openBySymbol
          .filter((position) => !previousActiveOperationIdsRef.current.has(position.order.id))
          .map((position) => ({
            id: position.order.id,
            createdAtMs: Date.parse(position.openedAt),
          })),
      ]

      if (newlyCreatedCandidates.length > 0) {
        const latestCreated = newlyCreatedCandidates.reduce((latest, candidate) => {
          return candidate.createdAtMs >= latest.createdAtMs ? candidate : latest
        })

        tradeDisplayLog('auto-selecting newly created operation', {
          operationId: latestCreated.id,
          backendSymbol: sym,
          previousSelectedOperationId: selectedOperationIdRef.current,
          candidateCount: newlyCreatedCandidates.length,
        })
        applySelection(latestCreated.id)
      }

      // Update the previous active operation IDs for next tick
      previousActiveOperationIdsRef.current = new Set(currentActiveOperationIds)
    }

    // Subscribe to store updates
    const unsubscribe = store.subscribe(handleStoreUpdate)

    // Initial state
    handleStoreUpdate()

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    }
  }, [applySelection, chartApi, clearSelection, dependencies])

  // Cleanup on unmount (Task 1.2)
  React.useEffect(() => {
    return () => {
      // Clear selection on teardown
      updateProviderSelection(null)
      lastSelectedSymbolRef.current = null

      // Unsubscribe from click handlers
      unsubscribeClickHandlers()
      restoreGroupSelectionHandlers()

      if (readonlySettingsEnforcementRafRef.current !== null) {
        cancelAnimationFrame(readonlySettingsEnforcementRafRef.current)
        readonlySettingsEnforcementRafRef.current = null
      }
      if (settingsChangedUnsubscribeRef.current) {
        settingsChangedUnsubscribeRef.current()
        settingsChangedUnsubscribeRef.current = null
      }
      if (layoutRestorationCleanupRef.current) {
        layoutRestorationCleanupRef.current()
        layoutRestorationCleanupRef.current = null
      }
      if (window.__utcTournamentChartDebug?.api === chartApiRef.current) {
        delete window.__utcTournamentChartDebug
      }
      chartApiRef.current = null
    }
  }, [restoreGroupSelectionHandlers, unsubscribeClickHandlers, updateProviderSelection])

  if (state.loading) {
    return (
      <WidgetShell debugMode={debugMode}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div>Loading tournament chart...</div>
        </div>
      </WidgetShell>
    )
  }

  if (state.error) {
    return (
      <WidgetShell debugMode={debugMode}>
        <ErrorCard error={state.error} />
      </WidgetShell>
    )
  }

  if (!dependencies) {
    return (
      <WidgetShell debugMode={debugMode}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div>Failed to initialize connection</div>
        </div>
      </WidgetShell>
    )
  }

  return (
    <WidgetShell debugMode={debugMode}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <LicensedChartWidgetHost
          key={chartRemountKey}
          dependencies={dependencies}
          onApiCreated={handleApiCreated}
          uiOverrides={TOURNAMENT_BASE_UI_OVERRIDES}
          className="tournament-chart-host"
        />
      </div>
    </WidgetShell>
  )
}
